import React from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { Alert } from 'antd';
import type { StoreState } from '../../data/store';
import type { ConnectionState } from '../../data/ws/WsManager';
import styles from './WsConnectionBanner.module.scss';

const BANNER_CONFIG: Partial<
  Record<
    ConnectionState,
    { message: string; type: 'success' | 'warning' | 'error' }
  >
> = {
  connected: {
    message: 'Connected',
    type: 'success',
  },
  connecting: {
    message: 'Connecting to server…',
    type: 'warning',
  },
  reconnecting: {
    message: 'Connection lost. Attempting to reconnect…',
    type: 'warning',
  },
  disconnected: {
    message: 'Disconnected from server. Reconnecting shortly…',
    type: 'error',
  },
  idle: {
    message: 'Disconnected from server.',
    type: 'error',
  },
};

export const WsConnectionBanner: React.FC = () => {
  const connectionState = useSelector(
    (s: StoreState) => s.websocket.connectionState
  );

  const [dismissed, setDismissed] = React.useState(false);

  React.useEffect(() => {
    setDismissed(false);
    const timer = window.setTimeout(() => {
      if (connectionState !== 'idle' && connectionState !== 'disconnected') {
        setDismissed(true);
      }
    }, 500);
    return () => window.clearTimeout(timer);
  }, [connectionState]);

  const config = BANNER_CONFIG[connectionState];

  if (!config) {
    return null;
  }

  return (
    <Alert
      className={clsx(
        styles.wsConnectionBanner,
        dismissed && styles.wsConnectionBannerDismissed
      )}
      data-testid="ws-connection-banner"
      title={config.message}
      type={config.type}
      banner
      showIcon
    />
  );
};
